import React, { useMemo, useRef } from 'react';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import { echartsContent, transChannel } from '@/pages/opinion/utils';
import {
  channelColorMap,
  chartAllColors,
  NO_DATE,
} from '@/pages/opinion/constants';
import {
  formatChartParamsDate,
  imgShowElement,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { useIntl } from 'umi';
import { formatTimeXAxisText } from '@/pages/opinion/utils/chart';
import { DBO_V } from '@op/utils/format';
import {
  channelInfo,
  dealwithChannelChartKey,
} from '@op/components/ChannelLabel';

export default function BarAndLine({
  chartType = 'bar',
  loading = true,
  filter = {},
  height,
  chartInfo,
  isEmpty = true,
  title = NO_DATE.EMPTY,
  isSingle = false,
}: {
  chartType?: 'bar' | 'line';
  loading?: boolean;
  filter?: any;
  height?: number;
  chartInfo: {
    xAxis: Array<string>;
    series: Array<{ name: string; data: Array<number> }>;
    legend: Array<string>;
    totalSeries: Array<number>;
  };
  isEmpty?: boolean;
  title?: string;
  isSingle?: boolean;
}) {
  const { formatMessage } = useIntl();
  const xAxisIntervalLimit = 8;
  const chartRef = useRef<HTMLDivElement>(null);
  const showTotalLabel = (totals: number[] = [], type: string = 'bar') => {
    const clientWidth = chartRef?.current?.clientWidth || 0;
    return totals.length <= 8 && type === 'bar' && clientWidth > 300;
  };

  const makeupOptions = () => {
    const { legend, xAxis, series, totalSeries } = chartInfo;
    let legendData = legend;
    let colorIndex = -1;
    function getColor(name: string) {
      const color = channelColorMap[name];
      if (color) return color;
      colorIndex++;
      return chartAllColors[colorIndex];
    }

    const showTotal = showTotalLabel(totalSeries, chartType);
    let seriesData: {
      itemStyle?: any;
      barMaxWidth?: string;
      barGap?: string;
      label?: any;
      name: string;
      type: string;
      stack?: string;
      data: (number | null)[];
    }[] = series.map(({ name, data }, index) => {
      let item: any = {
        name: name,
        type: chartType,
        stack: chartType === 'bar' ? 'Total' : '',
        data: data,
        itemStyle: { color: getColor(name) },
        barMaxWidth: '70px',
      };

      return item;
    });

    if (showTotal) {
      seriesData.push({
        name: 'Total',
        type: 'bar',
        stack: 'Total',
        data: totalSeries.map(() => 0),
        label: {
          show: true,
          position: 'top',
          formatter: (params: any) =>
            totalSeries[params.dataIndex]
              ? DBO_V.reviews(totalSeries[params.dataIndex])
              : '',
        },
        itemStyle: { color: 'rgba(0,0,0,0)' },
      });
    }

    const option = {
      // color: defaultColorArr,
      tooltip: {
        borderWidth: 0,
        padding: 0,
        confine: true,
        trigger: 'axis',
        extraCssText: 'z-index:8',
        formatter: (params: any[]) => {
          const { dataIndex, axisValue } = params[0] || {};
          const timeText = listTimeTitleFunc(axisValue, filter?.date_type);
          return formatChartParamsDate({
            params: params,
            beforeSort: true,
            listTitleFunc: (item: any) => {
              const isNoOther = item.seriesName !== 'Others';
              const { img, name, flag } = channelInfo(item.seriesName);

              if (!flag) {
                return imgShowElement(
                  img,
                  isNoOther ? name : formatMessage({ id: item.seriesName }),
                );
              }
              return formatMessage({ id: item.seriesName || NO_DATE.EMPTY });
            },
            dealwithRecords(records) {
              const total = totalSeries[dataIndex];

              const list = records.map((item) => {
                return {
                  ...item,
                  percentage: DBO_V.percentage(item.originValue, total, 2),
                };
              });

              if (!isSingle) {
                list.unshift({
                  text: formatMessage({ id: 'Sum' }),
                  value: DBO_V.reviews(total) as string,
                  percentage: total === 0 ? '-' : '100%',
                  originValue: total,
                  originText: 'Sum',
                });
              }

              return list.filter(
                (item) => item.originText !== formatMessage({ id: 'Total' }),
              );
            },
            tablesConfigFunc: (records: any[]) => {
              const columns = [
                {
                  header: timeText,
                  weight: 'bold',
                  key: 'text',
                },
                {
                  header: formatMessage({ id: title }),
                  weight: 'bold',
                  key: 'value',
                  align: 'right',
                },
              ];

              if (!isSingle) {
                columns.push({
                  header: formatMessage({ id: 'Share-1' }),
                  weight: 'bold',
                  key: 'percentage',
                  align: 'right',
                });
              }

              return [{ records, columns }];
            },
          });
        },
        // alwaysShowContent: true,
      },
      grid: {
        top: 20,
      },
      legend: {
        data: legendData,
        formatter: (params: string) => {
          return `{${dealwithChannelChartKey(params)}|} {name|${transChannel(
            params,
          )}}`;
        },
      },
      axisPointer: getAxisPointer((data) => {
        return formatTimeXAxisText(data.value, filter?.date_type);
      }),
      xAxis: {
        data: xAxis,
        axisLabel: {
          interval: xAxis?.length > xAxisIntervalLimit ? 'auto' : 0,
          fontSize: 10,
          showMaxLabel: true,
          formatter: (value: any) =>
            formatTimeXAxisText(value, filter?.date_type),
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: any) => echartsContent(value),
          fontSize: 11,
        },
      },
      series: seriesData,
    };

    return option;
  };

  const options = useMemo(() => {
    if (isEmpty) return {};

    return makeupOptions() || {};
  }, [chartType, formatMessage, chartInfo, isEmpty]);

  return (
    <div ref={chartRef}>
      <EChartCommon
        options={options}
        loading={loading}
        empty={isEmpty}
        optionsProps={{ type: chartType }}
        height={height}
      ></EChartCommon>
    </div>
  );
}
